import dayjs from "dayjs";
import styles from "../MoveOutChecklist.module.css";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { PhaseBanner } from "./CommonComponents";

const StatementTab = ({ leaseData, moveoutData, submitMoveoutChceckList, propertyDetails, setLoading, setTab }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [paymentData, setPaymentData] = useState(null);

    useEffect(() => {
        const fetchProperty = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/moveout-payment-data/" + leaseData?.tenant_id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setPaymentData(response.data.data);
            }).catch((error) => { });
        }
        if (leaseData?.tenant_id) {
            fetchProperty();
        }
    }, [leaseData?.tenant_id]);

    const totalBalance = parseFloat(leaseData?.security_amount) + parseFloat(paymentData?.paid_amount) - parseFloat(paymentData?.prorated_rent) - parseFloat(paymentData?.prev_balance) - parseFloat(moveoutData?.damage_cost);

    const onSubmit = () => {
        let data = { 'is_submitted': 1, 'is_completed': 1, current_step: 4, final_amount: totalBalance };

        submitMoveoutChceckList(data);
    }

    const totalDamageCost = moveoutData?.damages.reduce((total, item) => {
        return total + (parseFloat(item?.cost) || 0);
    }, 0);

    return <>
        <div className={`${styles.tp} ${styles.tpActive}`}>
            <PhaseBanner
                type="green"
                icon="receipt"
                title="Final Statement — Ready to send"
                sub={`Inspection on ${dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}. Sign below and send the final statement to issue ${totalBalance > 0 ? "a refund" : "a charge"} to the tenant.`}
            />

            {/* Rent adjustment */}
            <div className={styles.sc}>
                <div className={styles.scTitle}>
                    Rent adjustment — {dayjs(moveoutData?.moveout_date).format("MMM YYYY")}
                </div>
                <div className={styles.sr}><span className={styles.sk}>Move-out date</span><span className={styles.sv}>{dayjs(moveoutData?.moveout_date).format("MMM DD, YYYY")}</span></div>
                <div className={styles.sr}><span className={styles.sk}>Inspection date</span><span className={styles.sv}>{dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}</span></div>
                <div className={styles.sr}><span className={styles.sk}>Days occupied in month</span><span className={styles.sv}>{dayjs(moveoutData?.moveout_date).date()} of {dayjs(moveoutData?.moveout_date).daysInMonth()} days</span></div>
                {paymentData && paymentData?.bill_amount > 0 && <div className={styles.sr}><span className={styles.sk}>Monthly Bill</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.bill_amount)}</span></div>}
                {paymentData && paymentData?.prorated_rent > 0 && paymentData?.days !== paymentData?.occupied_days && <div className={styles.sr}><span className={styles.sk}>{"Pro-rated rent for " + dayjs(moveoutData?.moveout_date).date() + " days"}</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.prorated_rent)}</span></div>}
                {paymentData && paymentData?.paid_amount > 0 && <div className={styles.sr}><span className={styles.sk}>Paid</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(paymentData?.paid_amount)}</span></div>}
                {paymentData && (paymentData?.prorated_rent > paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Due of {dayjs(moveoutData?.moveout_date).format('MMM YYYY')}</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prorated_rent - paymentData?.paid_amount))}</span></div>}
                {paymentData && (paymentData?.prorated_rent <= paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Credit for {dayjs(moveoutData?.moveout_date).format('MMM YYYY')}</span><span className={`${styles.sv} ${styles.credit}`}>+{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.paid_amount - paymentData?.prorated_rent))}</span></div>}
            </div>

            {/* Damage detail */}
            {moveoutData?.damages.length > 0 && (
                <div className={styles.sc}>
                    <div className={styles.scTitle}>Damage detail</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid #E5E7EB` }}>
                                <th style={{ textAlign: "left", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Item</th>
                                <th style={{ textAlign: "right", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {moveoutData?.damages.map((d, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid #E5E7EB" }}>
                                    <td style={{ padding: "7px 0" }}>{d?.description || "—"}</td>
                                    <td style={{ padding: "7px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>${(d?.cost || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className={styles.sr}>
                        <span className={styles.sk}>Total damage charges</span>
                        <span className={`${styles.sv} ${styles.charge}`} style={{ fontSize: 16 }}>
                            {"-"}{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalDamageCost))}
                        </span>
                    </div>
                </div>
            )}

            {/* Deposit settlement */}
            <div className={styles.sc}>
                <div className={styles.scTitle}>Deposit settlement</div>
                <div className={styles.sr}><span className={styles.sk}>Security deposit held</span><span className={styles.sv}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((leaseData?.security_amount))}</span></div>
                {paymentData && (paymentData?.prorated_rent <= paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Rent credit</span><span className={`${styles.sv} ${styles.credit}`}>+{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.paid_amount - paymentData?.prorated_rent))}</span></div>}
                {paymentData && (paymentData?.prorated_rent > paymentData?.paid_amount) && <div className={styles.sr}><span className={styles.sk}>Rent charge</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prorated_rent - paymentData?.paid_amount))}</span></div>}
                {paymentData?.prev_balance >= 0 && <div className={styles.sr}><span className={styles.sk}>Previous Outstanding Balance</span><span className={`${styles.sv} ${styles.charge}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prev_balance))}</span></div>}
                {paymentData?.prev_balance < 0 && <div className={styles.sr}><span className={styles.sk}>Previous Outstanding Balance</span><span className={`${styles.sv} ${styles.credit}`}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((paymentData?.prev_balance * -1))}</span></div>}
                <div className={styles.sr}><span className={styles.sk}>Damage charges (tenant)</span><span className={`${styles.sv} ${moveoutData?.damage_cost > 0 ? styles.charge : ""}`}>-{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((moveoutData?.damage_cost))}</span></div>

                {/*<div className={styles.sr}>
                <span className={styles.sk}>Voided Invoice</span>
                <span className={`${styles.sv} ${styles.credit}`}>
                    June 2026
                </span>
            </div>*/}

                <div className={styles.sr}>
                    <span className={styles.sk}>{totalBalance > 0 ? "Refund to tenant" : "Balance due from tenant"}</span>
                    <span className={`${styles.sv} ${totalBalance > 0 ? styles.credit : styles.charge}`} style={{ fontSize: 16 }}>
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}
                    </span>
                </div>
            </div>

            {/* Verdict */}
            <div className={`${styles.verdict} ${totalBalance > 0 ? styles.verdictRefund : styles.verdictOwe}`}>
                <i className={`ti ${totalBalance > 0 ? "ti-circle-check" : "ti-alert-circle"} ${styles.verdictIcon}`} />
                <div>
                    <div className={styles.vt}>
                        {totalBalance > 0 ? `Refund of ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}` : `Collect balance of ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((totalBalance))}`}
                    </div>
                    <div className={styles.vs}>
                        {totalBalance > 0 ? "Post credit to tenant ledger. Transfer refund within 30 days of move-out." : "Raise final invoice. Collect before releasing deposit remainder."}
                    </div>
                </div>
            </div>

            <div className={styles.fa}>
                <button className={`${styles.btn} ${styles.btnS}`} onClick={() => { setTab(p => p - 1) }}>
                    <i className="ti ti-arrow-left" /> Back
                </button>
                <div className={styles.faRight}>
                    <button className={`${styles.btn} ${styles.btnS}`}>Cancel</button>
                    <button className={`${styles.btn} ${styles.btnP}`} onClick={onSubmit}>
                        <i className="ti ti-send" /> Send final statement
                    </button>
                </div>
            </div>
        </div>
    </>
}

export default StatementTab;